/*
 * Copyright (c) 2020. Sonrai Analytics Ltd.
 * Developed by Ryan Duffy
 */

import { Injectable } from '@angular/core';
import { MyNotificationModel } from '@app/models/my-notification.model';
import { ApiBaseService } from '@app/services/api-services/api-base.service';
import { HttpClient } from '@angular/common/http';
import { MyNotificationListService } from '@app/services/api-services/my-notifications/my-notifications-list.service';
import { ApiParamsModel } from '@app/models/api-params.model';
import { ApiFilterModel } from '@app/models/api-filter.model';
import { ApiSortModel } from '@app/models/api-sort.model';
import { AuthUserService } from '@app/services/authentication/auth-user-service';
@Injectable({
  providedIn: 'root',
})
export class MyNotificationService extends ApiBaseService {
  private _myNotification: MyNotificationModel;
  protected _endpoint = 'mynotifications';

  constructor(
    http: HttpClient,
    authUserService: AuthUserService,
    private myNotificationListService: MyNotificationListService
  ) {
    super(http, authUserService);
  }

  get myNotification(): MyNotificationModel {
    return this._myNotification;
  }

  set myNotification(value: MyNotificationModel) {
    this._myNotification = value;
  }

  /**
   *   Ryan Duffy
   *
   *  getMyNotificationById
   *
   * : Get an MyNotification based on the id by filtering the API query
   *
   * @param id of type string
   *
   * @returns Void: gets the MyNotification with the id that meets the query
   */
  public getMyNotificationById(myNotificationId: string): void {
    const apiParamsModel = new ApiParamsModel();
    const apiFilterModel: ApiFilterModel = {
      field: 'myNotification_id',
      operator: 'eq',
      value: myNotificationId,
    };
    apiParamsModel.filter = [apiFilterModel];
    this.getData(apiParamsModel);
  }
  /**
   *   Ryan Duffy
   *
   *  getMyNotificationByUserId
   *
   * : Get an MyNotification based on the id by filtering the API query
   *
   * @param id of type string
   *
   * @returns Void: gets the MyNotification with the id that meets the query
   */
  public getMyNotificationByUserIdByNewest(userId: string): void {
    const apiParamsModel = new ApiParamsModel();
    const apiFilterModel: ApiFilterModel = {
      field: 'user_id',
      operator: 'eq',
      value: userId,
    };
    const apiSortModel = new ApiSortModel();
    apiSortModel.field = 'created';
    apiSortModel.direction = 'desc';
    apiParamsModel.filter = [apiFilterModel];
    apiParamsModel.sort = [apiSortModel];
    this.getData(apiParamsModel);
  }
  /**
   *   Ryan Duffy
   *
   *  getMyNotificationByUserId
   *
   * : Get an MyNotification based on the id by filtering the API query
   *
   * @param id of type string
   *
   * @returns Void: gets the MyNotification with the id that meets the query
   */
  public getMyNotificationByUserId(userId: string): void {
    const apiParamsModel = new ApiParamsModel();
    const apiFilterModel: ApiFilterModel = {
      field: 'user_id',
      operator: 'eq',
      value: userId,
    };
    apiParamsModel.filter = [apiFilterModel];
    this.getData(apiParamsModel);
  }
  /**
   *   Ryan Duffy
   *
   *  getMyNewNotificationsById
   *
   * : Get new MyNotifications based on the id by filtering the API query
   *
   * @param id of type string
   *
   * @returns Void: gets the MyNotification with the id that meets the query
   */
  public getMyNewNotificationByUserId(userId: string): void {
    const apiParamsModel = new ApiParamsModel();
    const apiFilterModel: ApiFilterModel = {
      field: ['user_id', 'notification_read'],
      operator: ['eq', 'eq'],
      value: [userId, false],
      joinOperator: 'AND',
    };
    apiParamsModel.filter = [apiFilterModel];
    this.getData(apiParamsModel);
  }

  /**
   *   Ryan Duffy
   *
   *  getMyNotificationByUserId
   *
   * : Get an MyNotification based on the id by filtering the API query
   *
   * @param id of type string
   *
   * @returns Void: gets the MyNotification with the id that meets the query
   */
  public getMyNotificationFromListByUserId(userId: string): void {
    if (this.myNotificationListService.myNotificationList.length > 0) {
      this.dataSubject.next(this.myNotificationListService.getMyNotificationsByUserID(userId));
    } else {
      this.getMyNotificationByUserId(userId);
      this.myNotificationListService.getData({});
    }
  }

  /**
  /**
   *  Ryan Duffy
   *
   *  handleData
   *
   * : handles the data from the list by extending the handleData function from the API base service
   *
   * @param data of type MyNotificationModel
   *
   * @returns Void: handle a get request from the postgres DB
   */
  protected handleData(data: MyNotificationModel[]): void {
    this.myNotification = data[0];
    super.handleData(data);
  }
  /**
   *   Ryan Duffy
   *
   *  handleInsert
   *
   * : handles the insert of a new MyNotification into the list
   *
   * @param data of type MyNotificationModel
   *
   * @returns Void: inserts a new MyNotification into the lsit
   */
  protected handleInsert(data: MyNotificationModel[]): void {
    this.myNotification = data[0];
    this.myNotificationListService.addToList(data[0]);
    super.handleInsert(data);
  }
  /**
   *   Ryan Duffy
   *
   *  handleUpdate
   *
   * : handles the update of an MyNotification in the list
   *
   * @param data of type MyNotificationModel
   *
   * @returns Void: update the lsit
   */
  protected handleUpdate(data: MyNotificationModel[]): void {
    this.myNotification = data[0];
    this.myNotificationListService.updateInList(data[0]);
    super.handleUpdate(data);
  }
  /**
   *   Ryan Duffy
   *
   *  handleDelete
   *
   * : deletes an MyNotification from the list
   *
   * @param data of type MyNotificationModel
   *
   * @returns Void: deletes a MyNotification from the list
   */
  protected handleDelete(data: MyNotificationModel[]): void {
    this.myNotificationListService.removeFromList(data[0]);
    super.handleDelete(data);
  }
}
